import React, { useState, useContext, useEffect } from 'react'
import {
  Apple,
  Tablet,
  Smartphone,
  Globe,
  Laptop,
  Computer,
  MonitorSmartphone,
  Kanban,
  Package,
  Plus,
  Upload,
  Link,
  X,
  Pencil,
  Trash2,
  GripVertical,
  Video,
  ArrowRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/lib/utils'
import { DashboardContext } from '@/context/DashboardContext'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import toast from 'react-hot-toast'
import Uppy from '@uppy/core'
import { Dashboard } from '@uppy/react'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import { RemoteConfigContext } from '@/context/RemoteConfigContext'
import RemoteSources from '@uppy/remote-sources'
import FirebaseStorageUploadDocuments from '@/utils/FirebaseStorageUploadDocuments'
import AwsS3 from '@uppy/aws-s3'
import { DeleteProductDialog } from './deleteProductDialog'
import { uploadProductLogo } from '@/utils/uploadProductLogo'

interface Product {
  id: string
  name: string
  tagline: string
  description: string
  uniqueSellingProposition?: string
  status: string
  platform: string
  projects: number
  sessions?: number
  logo: string | null
  createdAt?: number
  projectsData: any[]
  order: number
  documents: Document[]
  links: any[]
}

interface Document {
  size: number
  storedFileName: string
  type: string
  fileName: string
}

interface SortableProductRowProps {
  product: Product
  onEdit: (product: Product) => void
  onDelete: (productId: string) => void
}

const SortableProductRow = ({ product, onEdit, onDelete }: SortableProductRowProps) => {
  const [isHovered, setIsHovered] = useState(false)

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: product.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const getPlatformIcon = (platform: string, size = 16, className = 'text-gray-700') => {
    switch (platform) {
      case 'iOS':
        return <Apple size={size} className={className} strokeWidth={2} />
      case 'iPad OS':
        return <Tablet size={size} className={className} strokeWidth={2} />
      case 'Android':
        return <Smartphone size={size} className={className} strokeWidth={2} />
      case 'Web':
        return <Globe size={size} className={className} strokeWidth={2} />
      case 'Mac OS':
        return <Laptop size={size} className={className} strokeWidth={2} />
      case 'Windows':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Linux':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Multi-Platform':
        return <MonitorSmartphone size={size} className={className} strokeWidth={2} />
      default:
        return <Globe size={size} className={className} strokeWidth={2} />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
      case 'Live':
        return 'text-[#B2C920]'
      case 'Beta':
        return 'text-[#964EB2]'
      case 'Alpha':
        return 'text-[#CF3F75]'
      case 'Planning':
        return 'text-[#2C8FB1]'
      case 'Ideation':
        return 'text-[#DCAE1D]'
      default:
        return 'text-gray-500'
    }
  }

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'Active':
      case 'Live':
        return 'bg-[#B2C920]/20 text-[#B2C920] border border-[#B2C920]/30'
      case 'Beta':
        return 'bg-[#964EB2]/20 text-[#964EB2] border border-[#964EB2]/30'
      case 'Alpha':
        return 'bg-[#CF3F75]/20 text-[#CF3F75] border border-[#CF3F75]/30'
      case 'Planning':
        return 'bg-[#2C8FB1]/20 text-[#2C8FB1] border border-[#2C8FB1]/30'
      case 'Ideation':
        return 'bg-[#DCAE1D]/20 text-[#DCAE1D] border border-[#DCAE1D]/30'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const platformIconColorClass = getStatusColor(product.status)

  return (
    <tr
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group hover:bg-gray-50 transition-colors"
    >
      <td className="p-4 relative">
        <div
          className="absolute left-0 p-1 rounded-md bg-white/80 cursor-grab z-20 opacity-0 group-hover:opacity-100 transition-opacity"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} className="text-gray-500" />
        </div>
      </td>

      <td className="p-4">
        <div className="w-16 h-16 relative rounded-md overflow-hidden">
          {product.logo ? (
            <img src={product.logo} alt={`${product.name} Product Logo`} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              {getPlatformIcon(product.platform, 32, platformIconColorClass)}
            </div>
          )}
        </div>
      </td>

      <td className="p-4 max-w-[300px]">
        <div>
          <h3 className="text-lg font-medium line-clamp-2">{product.name}</h3>
          <p className="text-sm text-gray-500 italic line-clamp-2">{product.tagline}</p>
        </div>
      </td>

      <td className="p-4">
        <span
          className={`inline-block px-3 py-1.5 text-sm font-medium rounded-full ${getStatusBadgeClasses(product.status)}`}
        >
          {product.status}
        </span>
      </td>

      <td className="p-4 min-w-[140px]">
        <div className="flex items-center gap-2 text-sm">
          {getPlatformIcon(product.platform, 20)}
          <span>{product.platform}</span>
        </div>
      </td>

      <td className="p-4">
        <div className="flex items-center gap-2 text-sm">
          <Kanban size={18} className="text-gray-600" />
          <span>{product.projects}</span>
        </div>
      </td>

      <td className="p-4">
        <div className="flex items-center gap-2 text-sm">
          <Video size={18} className="text-gray-600" />
          <span>{product.sessions || 0}</span>
        </div>
      </td>

      <td className="p-4">
        <div className="flex gap-2 invisible group-hover:visible">
          <Button onClick={() => onEdit(product)} size="sm" variant="ghost" className="h-8 w-8 p-0">
            <Pencil size={16} className="text-gray-600" />
          </Button>
          <Button onClick={() => onDelete(product.id)} size="sm" variant="ghost" className="h-8 w-8 p-0">
            <Trash2 size={16} className="text-gray-600" />
          </Button>
        </div>
      </td>
    </tr>
  )
}

const Products = () => {
  const { config, isLoading: remoteConfigLoading } = useContext(RemoteConfigContext)
  const { products, setProducts, setAllProducts } = useContext(DashboardContext)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [currentProductId, setCurrentProductId] = useState<string | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [productName, setProductName] = useState('')
  const [platform, setPlatform] = useState('')
  const [status, setStatus] = useState('ideation')
  const [tagline, setTagline] = useState('')
  const [description, setDescription] = useState('')
  const [uniqueSellingProposition, setUniqueSellingProposition] = useState('')
  const [documents, setDocuments] = useState<Document[]>([])
  const [links, setLinks] = useState<any[]>([])
  const [newLink, setNewLink] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const customFetch = useCustomFetch()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const remoteSources = []
  if (!remoteConfigLoading) {
    if (config.googleDrive) remoteSources.push('GoogleDrive')
    if (config.oneDrive) remoteSources.push('OneDrive')
    if (config.zoom) remoteSources.push('Zoom')
    if (config.dropbox) remoteSources.push('Dropbox')
    if (config.box) remoteSources.push('Box')
  }

  const [uppy] = useState(() => {
    const newUppy = new Uppy({
      autoProceed: false,
      debug: true,
      restrictions: {
        maxFileSize: 10 * 1024 * 1024,
        maxNumberOfFiles: 10,
        allowedFileTypes: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
      },
    })
      .use(RemoteSources, {
        companionUrl: import.meta.env.VITE_COMPANION_URL,
        sources: remoteSources,
      })
      .use(FirebaseStorageUploadDocuments, {})

    newUppy.on('file-added', (file) => {
      if (file.source && ['GoogleDrive', 'Zoom', 'Dropbox', 'OneDrive'].includes(file.source)) {
        newUppy.use(AwsS3, {
          endpoint: import.meta.env.VITE_COMPANION_URL,
        })
      } else {
        if (newUppy.getPlugin(AwsS3.name)) {
          newUppy.removePlugin(AwsS3 as any)
        }
      }
    })

    return newUppy
  })

  useEffect(() => {
    uppy.on('complete', async (result) => {
      try {
        if (result.successful.length) {
          const files = []
          for (const file of result.successful) {
            if (file.isRemote) {
              console.log('file', file)
              const fileLocation = file?.response?.body?.Key as string
              // removing folder meets/ to get only the file name
              const storedFileName = fileLocation.substring(6)

              files.push({ size: file.size, storedFileName, type: file.type, fileName: file.name })
            } else {
              files.push({
                size: file.size,
                storedFileName: file.uploadURL,
                type: file.type,
                fileName: file.name,
              })
            }
          }
          if (!files.length) return

          setDocuments((prevDocuments) => [...prevDocuments, ...files])

          toast.success('Files uploaded!', {
            duration: 3000,
          })
        }
      } catch (error) {
        console.error('Error uploading files', error)
        toast.error('Whoops! Something went wrong. Please try again.')
      }
    })

    return () => uppy.destroy()
  }, [])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      let updatedItems = []
      setProducts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)

        updatedItems = arrayMove(items, oldIndex, newIndex)

        return updatedItems.map((item: Product, index: number) => ({ ...item, order: index + 1 }))
      })

      customFetch('/changeProductsOrder', 'PUT', { productIds: updatedItems.map((item) => item.id) })
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      try {
        setIsUploading(true)
        setUploadProgress(0)
        const downloadURL = await uploadProductLogo(file, (progress) => {
          setUploadProgress(progress)
        })
        setSelectedImage(downloadURL)
        toast.success('Logo uploaded successfully')
      } catch (error) {
        console.error('Error uploading logo:', error)
        toast.error('Failed to upload logo')
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    }
  }

  const handleRemoveDocument = (storedFileName: string) => {
    setDocuments(documents.filter((doc) => doc.storedFileName !== storedFileName))
  }

  const handleAddLink = () => {
    if (newLink.trim() !== '') {
      setLinks([...links, newLink])
      setNewLink('')
    }
  }

  const handleRemoveLink = (linkToDelete: string) => {
    setLinks(links.filter((link) => link !== linkToDelete))
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
  }

  const resetForm = () => {
    setProductName('')
    setPlatform('')
    setStatus('ideation')
    setTagline('')
    setDescription('')
    setUniqueSellingProposition('')
    setSelectedImage(null)
    setDocuments([])
    setLinks([])
    setNewLink('')
    setCurrentProductId(null)
  }

  const openAddDialog = () => {
    resetForm()
    setAddDialogOpen(true)
  }

  const handleEditProduct = (product: Product) => {
    setProductName(product.name)
    setTagline(product.tagline)
    setDescription(product.description)
    setUniqueSellingProposition(product.uniqueSellingProposition || '')
    setSelectedImage(product.logo)
    setDocuments(product.documents || [])
    setLinks(product.links || [])

    const platformValue = product.platform.toLowerCase().replace(' ', '').replace('-', '')
    setPlatform(platformValue)

    let statusValue = product.status.toLowerCase()
    if (statusValue === 'live') statusValue = 'active'
    setStatus(statusValue)

    setCurrentProductId(product.id)

    setEditDialogOpen(true)
  }

  const handleDeleteProduct = (productId: string) => {
    setProductToDelete(productId)
    setIsDeleteDialogOpen(true)
  }

  const confirmDeleteProduct = async (productId: string) => {
    await customFetch(`/deleteProduct/${productId}`, 'DELETE')
    setAllProducts()
    toast.success('Product deleted successfully')
  }

  const handleCreateProduct = async () => {
    if (!productName.trim()) return toast.error('Product name is required')
    if (!tagline.trim()) return toast.error('Tagline is required')
    if (!platform.trim()) return toast.error('Platform is required')
    if (!status.trim()) return toast.error('Status is required')
    if (!description.trim()) return toast.error('Description is required')
    if (!uniqueSellingProposition.trim()) return toast.error('Unique Selling Proposition is required')

    const newProduct = {
      name: productName,
      tagline: tagline,
      description: description,
      uniqueSellingProposition,
      status: status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
      platform:
        platform === 'web'
          ? 'Web'
          : platform === 'ios'
            ? 'iOS'
            : platform === 'android'
              ? 'Android'
              : platform === 'macos'
                ? 'Mac OS'
                : platform === 'windows'
                  ? 'Windows'
                  : platform === 'linux'
                    ? 'Linux'
                    : platform === 'multiplatform'
                      ? 'Multi-Platform'
                      : 'Web',
      ...(selectedImage ? { logo: selectedImage } : {}),
      order: products.length + 1,
      links,
      documents,
    }

    // create product in db get the just created product id
    const response = await customFetch('/createProduct', 'POST', { ...newProduct })

    setProducts((prevProducts) => [...prevProducts, { ...newProduct, id: response.id, projectsData: [] }])

    resetForm()
    setAddDialogOpen(false)
  }

  const handleSaveProduct = async () => {
    if (!productName.trim() || !currentProductId) return toast.error('Product name is required')
    if (!tagline.trim()) return toast.error('Tagline is required')
    if (!platform.trim()) return toast.error('Platform is required')
    if (!status.trim()) return toast.error('Status is required')
    if (!description.trim()) return toast.error('Description is required')
    if (!uniqueSellingProposition.trim()) return toast.error('Unique Selling Proposition is required')

    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        if (product.id === currentProductId) {
          return {
            ...product,
            name: productName,
            tagline: tagline,
            description: description,
            uniqueSellingProposition,
            status: status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
            platform:
              platform === 'web'
                ? 'Web'
                : platform === 'ios'
                  ? 'iOS'
                  : platform === 'android'
                    ? 'Android'
                    : platform === 'macos'
                      ? 'Mac OS'
                      : platform === 'windows'
                        ? 'Windows'
                        : platform === 'linux'
                          ? 'Linux'
                          : platform === 'multiplatform'
                            ? 'Multi-Platform'
                            : 'Web',
            logo: selectedImage,
            links,
            documents,
          }
        }
        return product
      })
    )

    await customFetch('/updateProduct', 'PUT', {
      id: currentProductId,
      name: productName,
      tagline,
      description,
      uniqueSellingProposition,
      links,
      documents,
      status: status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
      platform:
        platform === 'web'
          ? 'Web'
          : platform === 'ios'
            ? 'iOS'
            : platform === 'android'
              ? 'Android'
              : platform === 'macos'
                ? 'Mac OS'
                : platform === 'windows'
                  ? 'Windows'
                  : platform === 'linux'
                    ? 'Linux'
                    : platform === 'multiplatform'
                      ? 'Multi-Platform'
                      : 'Web',
      ...(selectedImage ? { logo: selectedImage } : {}),
    })

    resetForm()
    setEditDialogOpen(false)
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="overflow-auto p-8 flex items-center justify-center">
        <div className="w-full max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-2">
            <h1 className="text-3xl font-bold">Products</h1>
            <Button className="bg-[#FF4A8E] text-white hover:bg-[#FF4A8E]/90" onClick={openAddDialog}>
              <Plus size={16} />
              Add
            </Button>
          </div>
          <p className="text-gray-500 mb-8">Manage and monitor your product portfolio</p>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="w-full bg-white rounded-xl shadow-sm border overflow-hidden border border-[#E4E4E4]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E4E4E4] bg-gray-50">
                    <th className="w-10 p-4"></th>
                    <th className="text-left p-4 font-medium text-gray-600">Logo</th>
                    <th className="text-left p-4 font-medium text-gray-600 max-w-[300px]">Product</th>
                    <th className="text-left p-4 font-medium text-gray-600">Status</th>
                    <th className="text-left p-4 font-medium text-gray-600 min-w-[140px]">Platform</th>
                    <th className="text-left p-4 font-medium text-gray-600">Projects</th>
                    <th className="text-left p-4 font-medium text-gray-600">Meetings</th>
                    <th className="text-left p-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={products.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                    {products.map((product) => (
                      <SortableProductRow
                        key={product.id}
                        product={product}
                        onEdit={handleEditProduct}
                        onDelete={handleDeleteProduct}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        </div>
      </div>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-[900px] rounded-2xl h-[80vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-xl font-semibold">Add New Product</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid gap-6">
              {/* Logo & Product details */}
              <div className="grid grid-cols-[120px_1fr] gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Logo</label>
                  <div className="h-[120px] w-[120px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative">
                    {selectedImage ? (
                      <div className="relative w-full h-full">
                        <img
                          src={selectedImage}
                          alt="Selected Logo"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md"
                        >
                          <X size={16} className="text-gray-600" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center p-2">
                        <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                        <p className="text-xs text-gray-600">
                          <label className="text-[#FF4A8E] hover:underline cursor-pointer">
                            Add logo
                            <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                          </label>
                        </p>
                        {isUploading && (
                          <div className="mt-2 w-full px-2">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-[#FF4A8E] h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{Math.round(uploadProgress)}%</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="product-name" className="text-sm font-medium">
                      Name*
                    </label>
                    <input
                      id="product-name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter product name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="tagline" className="text-sm font-medium">
                      Tagline*
                    </label>
                    <input
                      id="tagline"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter a short, catchy tagline"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="platform" className="text-sm font-medium">
                    Platform*
                  </label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger id="platform">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web">
                        <div className="flex items-center gap-2">
                          <Globe size={16} className="text-gray-700" />
                          <span>Web</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="ios">
                        <div className="flex items-center gap-2">
                          <Apple size={16} className="text-gray-700" />
                          <span>iOS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="android">
                        <div className="flex items-center gap-2">
                          <Smartphone size={16} className="text-gray-700" />
                          <span>Android</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="macos">
                        <div className="flex items-center gap-2">
                          <Laptop size={16} className="text-gray-700" />
                          <span>Mac OS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="windows">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Windows</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="linux">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Linux</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="multiplatform">
                        <div className="flex items-center gap-2">
                          <MonitorSmartphone size={16} className="text-gray-700" />
                          <span>Multi-Platform</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="status" className="text-sm font-medium">
                    Status*
                  </label>
                  <Select value={status} onValueChange={setStatus} defaultValue="ideation">
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ideation">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#DCAE1D]"></span>
                          <span>Ideation</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="planning">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#2C8FB1]"></span>
                          <span>Planning</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="alpha">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#CF3F75]"></span>
                          <span>Alpha</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="beta">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#964EB2]"></span>
                          <span>Beta</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="active">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#B2C920]"></span>
                          <span>Active</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  Description*
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Describe your product in detail"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="usp" className="text-sm font-medium">
                  Unique Selling Proposition*
                </label>
                <textarea
                  id="usp"
                  value={uniqueSellingProposition}
                  onChange={(e) => setUniqueSellingProposition(e.target.value)}
                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="What makes your product special? What's your competitive advantage?"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Documents</label>
                  <div className="border rounded-lg overflow-hidden h-[180px]">
                    <Dashboard
                      target="#uppy-dashboard"
                      uppy={uppy}
                      plugins={remoteSources}
                      showProgressDetails={true}
                      note="Acceptable file types: PDF, Word, PPT, Excel (Max 10MB)"
                      width="100%"
                      height="180px"
                    />
                  </div>
                  {documents.length > 0 && (
                    <div className="mt-4 max-h-[80px] overflow-y-auto">
                      {documents.map((doc) => (
                        <div
                          key={doc.storedFileName}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-md mb-2"
                        >
                          <span className="text-sm text-gray-600">{doc.fileName}</span>
                          <button
                            onClick={() => handleRemoveDocument(doc.storedFileName)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Links</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="https://example.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newLink.trim()) {
                          e.preventDefault()
                          handleAddLink()
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleAddLink}
                      disabled={!newLink.trim()}
                      className="bg-[#FF4A8E] text-white hover:bg-[#FF4A8E]/90 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-gray-400 transition-colors"
                    >
                      <ArrowRight size={16} />
                    </Button>
                  </div>

                  {links.length > 0 && (
                    <div className="h-[120px] overflow-y-auto space-y-2">
                      {links.map((link, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                          <Link size={14} className="text-gray-500" />
                          <span className="text-sm text-gray-600 flex-1">{link}</span>
                          <button onClick={() => handleRemoveLink(link)} className="text-gray-400 hover:text-gray-600">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t mt-auto">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-[#FF4A8E] hover:bg-[#FF4A8E]/90" onClick={handleCreateProduct}>
              Create Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-[900px] rounded-2xl h-[80vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-xl font-semibold">Edit Product</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid gap-6">
              {/* Logo & Product details */}
              <div className="grid grid-cols-[120px_1fr] gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Logo</label>
                  <div className="h-[120px] w-[120px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative">
                    {selectedImage ? (
                      <div className="relative w-full h-full">
                        <img
                          src={selectedImage}
                          alt="Selected Logo"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md"
                        >
                          <X size={16} className="text-gray-600" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center p-2">
                        <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                        <p className="text-xs text-gray-600">
                          <label className="text-[#FF4A8E] hover:underline cursor-pointer">
                            Add logo
                            <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                          </label>
                        </p>
                        {isUploading && (
                          <div className="mt-2 w-full px-2">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-[#FF4A8E] h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{Math.round(uploadProgress)}%</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="edit-product-name" className="text-sm font-medium">
                      Name*
                    </label>
                    <input
                      id="edit-product-name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter product name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="edit-tagline" className="text-sm font-medium">
                      Tagline*
                    </label>
                    <input
                      id="edit-tagline"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter a short, catchy tagline"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="edit-platform" className="text-sm font-medium">
                    Platform*
                  </label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger id="edit-platform">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web">
                        <div className="flex items-center gap-2">
                          <Globe size={16} className="text-gray-700" />
                          <span>Web</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="ios">
                        <div className="flex items-center gap-2">
                          <Apple size={16} className="text-gray-700" />
                          <span>iOS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="android">
                        <div className="flex items-center gap-2">
                          <Smartphone size={16} className="text-gray-700" />
                          <span>Android</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="macos">
                        <div className="flex items-center gap-2">
                          <Laptop size={16} className="text-gray-700" />
                          <span>Mac OS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="windows">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Windows</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="linux">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Linux</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="multiplatform">
                        <div className="flex items-center gap-2">
                          <MonitorSmartphone size={16} className="text-gray-700" />
                          <span>Multi-Platform</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="edit-status" className="text-sm font-medium">
                    Status*
                  </label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="edit-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ideation">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#DCAE1D]"></span>
                          <span>Ideation</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="planning">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#2C8FB1]"></span>
                          <span>Planning</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="alpha">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#CF3F75]"></span>
                          <span>Alpha</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="beta">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#964EB2]"></span>
                          <span>Beta</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="active">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#B2C920]"></span>
                          <span>Active</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-description" className="text-sm font-medium">
                  Description*
                </label>
                <textarea
                  id="edit-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Describe your product in detail"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-usp" className="text-sm font-medium">
                  Unique Selling Proposition*
                </label>
                <textarea
                  id="edit-usp"
                  value={uniqueSellingProposition}
                  onChange={(e) => setUniqueSellingProposition(e.target.value)}
                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="What makes your product special? What's your competitive advantage?"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Documents</label>
                  <div className="border rounded-lg overflow-hidden h-[180px]">
                    <Dashboard
                      target="#uppy-dashboard"
                      uppy={uppy}
                      plugins={remoteSources}
                      showProgressDetails={true}
                      note="Acceptable file types: PDF, Word, PPT, Excel (Max 10MB)"
                      width="100%"
                      height="180px"
                    />
                  </div>
                  {documents.length > 0 && (
                    <div className="mt-4 max-h-[80px] overflow-y-auto">
                      {documents.map((doc) => (
                        <div
                          key={doc.storedFileName}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-md mb-2"
                        >
                          <span className="text-sm text-gray-600">{doc.fileName}</span>
                          <button
                            onClick={() => handleRemoveDocument(doc.storedFileName)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Links</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="https://example.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newLink.trim()) {
                          e.preventDefault()
                          handleAddLink()
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleAddLink}
                      disabled={!newLink.trim()}
                      className="bg-[#FF4A8E] text-white hover:bg-[#FF4A8E]/90 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-gray-400 transition-colors"
                    >
                      <ArrowRight size={16} />
                    </Button>
                  </div>

                  {links.length > 0 && (
                    <div className="h-[120px] overflow-y-auto space-y-2">
                      {links.map((link, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                          <Link size={14} className="text-gray-500" />
                          <span className="text-sm text-gray-600 flex-1">{link}</span>
                          <button onClick={() => handleRemoveLink(link)} className="text-gray-400 hover:text-gray-600">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t mt-auto">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-[#FF4A8E] hover:bg-[#FF4A8E]/90" onClick={handleSaveProduct}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isDeleteDialogOpen && productToDelete && (
        <DeleteProductDialog
          handleDeleteProduct={confirmDeleteProduct}
          productId={productToDelete}
          product={products.find((p) => p.id === productToDelete)}
          handleClose={() => {
            setIsDeleteDialogOpen(false)
            setProductToDelete(null)
          }}
        />
      )}
    </div>
  )
}

export default Products
